
import { BlogPost } from './types';

export const blogPosts: BlogPost[] = [
  {
    id: 1,
    title: "Jerry Fox: The Blind Brooklyn Cat Who Saved Borough Hall",
    excerpt: "The remarkable story of a blind tabby cat who patrolled Brooklyn Borough Hall for nearly 30 years and saved the building from fire in 1904.",
    content: `If you had walked past Brooklyn Borough Hall in the late 1800s, you might've seen a striped tabby pacing the grounds like he owned the place. His name was Jerry Fox, and by all accounts, he kind of did.

Jerry wasn't just another neighborhood cat. He was a fixture of the Brooklyn civic center for nearly three decades—from around 1876 to 1904—spending his days patrolling the area outside Terry Fox's café and making rounds through Borough Hall like a city employee on a tight schedule.

In 1903, Jerry lost his eyesight. Most cats would have faded into the background—but not Jerry. A local optometrist actually made him a tiny pair of glasses, which he wore proudly. Even blind, he continued his daily walks. Trolley drivers, court staff, bridge workers, police—they all knew Jerry. They looked out for him, and he looked out for them.

## The Day That Changed Everything

Then came May 22, 1904.

That morning, inside an office at Borough Hall, a judge had left a lit cigar behind. It smoldered, caught fire, and started burning important municipal documents. No one was around—except Jerry.

Despite being nearly blind, he sensed something was off. He howled and meowed until someone came to investigate. Thanks to Jerry's noise, the fire was discovered in time. Borough Hall was saved. So were the records.

A cat—elderly, blind, wearing glasses—saved a government building.

## Why Jerry Matters

You won't find a plaque for Jerry Fox in Borough Hall. There's no mention of him in the building's official history. But you will hear his story on our tours. Because we think he matters.

We've launched a petition to change that. Jerry Fox deserves formal recognition. A plaque. A small one, near the spot where it happened. Something to let people know that a cat once saved this city.

**[Sign the petition to honor Jerry Fox](https://docs.google.com/forms/d/1Q7iGAsbktYpAboxPOJq4GUhic8RE89UA-PagAFk1Lh8/edit)**`,
    image: "/lovable-uploads/15d7c76f-2960-4c03-b123-b6dec630aaa4.png",
    date: "June 19, 2025",
    author: "Cats About Town Tours",
    category: "Cat History",
    tags: ["Jerry Fox", "Brooklyn Borough Hall", "cat hero", "Brooklyn history", "fire rescue", "blind cat"]
  },
  {
    id: 2,
    title: "The Cats of Wall Street: Financial District Felines Through History",
    excerpt: "Discover the fascinating stories of cats who made their mark in New York's Financial District, from ship cats to office mousers.",
    content: `# The Cats of Wall Street: Financial District Felines Through History

New York's Financial District has always been a place of business, but it's also been home to some remarkable cats throughout history. From the early days of New Amsterdam to the modern skyscrapers of today, felines have played important roles in the area's story.

## Ship Cats and Harbor Workers

In the 17th and 18th centuries, when the Financial District was primarily a bustling port, ship cats were essential crew members. These seafaring felines kept ships free of rats and mice that could damage cargo and spread disease.

Many of these cats would eventually make their way onto the docks and into the warehouses that lined the waterfront. They became part of the working community, earning their keep by controlling pest populations.

## The Rise of Office Cats

As the Financial District evolved into the financial center we know today, cats found new roles. Many banks and trading houses kept cats to protect important documents and supplies from rodent damage.

Some notable feline residents included:

- **Tabitha**, who lived at the New York Stock Exchange in the 1890s
- **Wellington**, the famous mouser of the First National Bank
- **Princess**, who had the run of several Wall Street offices in the early 1900s

## Modern Financial Felines

While office cats are less common today due to health regulations, the tradition hasn't completely disappeared. Some financial firms still maintain relationships with local cat rescue organizations, and there are several cat cafés in the broader Financial District area.

The legacy of these working cats reminds us that even in the most serious business environments, there's always been room for the practical wisdom and companionship that cats provide.

*Want to learn more about the cats of the Financial District? Join us on our Financial District Cat Tour to discover hidden feline history throughout lower Manhattan.*`,
    image: "/lovable-uploads/0390c64c-61a9-4a20-8c0a-ad3de146e1c9.png",
    date: "May 15, 2024",
    author: "Cats About Town Tours",
    category: "Cat History",
    tags: ["Wall Street", "Financial District", "working cats", "ship cats", "office cats", "NYC history"]
  },
  {
    id: 3,
    title: "Brooklyn Heights: A Haven for Historic Cats",
    excerpt: "Explore the tree-lined streets of Brooklyn Heights and discover the stories of the neighborhood cats who've called this historic area home.",
    content: `# Brooklyn Heights: A Haven for Historic Cats

Brooklyn Heights, with its tree-lined promenade and historic brownstones, has long been one of New York's most cat-friendly neighborhoods. The area's quiet streets and garden spaces have provided perfect homes for countless felines throughout the centuries.

## The Promenade Cats

The Brooklyn Heights Promenade, built in the 1950s, quickly became a favorite spot for both residents and their cats. The elevated walkway offers stunning views of Manhattan, but for cats, it provided something even better: a safe, enclosed space to enjoy the outdoors.

Many longtime residents remember the "Promenade Cats" - a loose community of neighborhood cats who would gather along the walkway, especially in the early morning and evening hours.

## Literary Cats of the Heights

Brooklyn Heights has been home to many famous writers, and where there are writers, there are often cats. The neighborhood's literary history includes several notable feline companions:

- **Jeoffry**, who belonged to poet Hart Crane during his time on Columbia Heights
- **The Mailer Cats**, a succession of felines who lived with author Norman Mailer
- **Dickens**, the beloved companion of a local mystery writer who lived on Remsen Street

## Garden Cats and Green Spaces

Despite being in the heart of Brooklyn, the Heights has always maintained numerous small gardens and green spaces. These areas became natural habitats for cats, both owned and feral.

The community has a long tradition of caring for these garden cats, with informal networks of residents ensuring they're fed and looked after. This spirit of community care extends to today, with several active TNR (Trap-Neuter-Return) programs operating in the area.

## Modern Cat Culture

Today, Brooklyn Heights continues to embrace its feline residents. Local businesses are cat-friendly, and the neighborhood hosts an annual "Cat Walk" fundraiser for local animal shelters.

The area's historic character and strong sense of community make it an ideal place for both cats and cat lovers to call home.

*Experience the cat-friendly charm of Brooklyn Heights on our guided tour, where we'll share stories of the neighborhood's feline residents past and present.*`,
    image: "/lovable-uploads/4d0e5940-dd93-4b5b-8b5f-4368292bc0e5.jpg",
    date: "April 3, 2024",
    author: "Cats About Town Tours",
    category: "Neighborhood Cats",
    tags: ["Brooklyn Heights", "Promenade", "literary cats", "garden cats", "neighborhood history", "cat community"]
  }
];
